import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, HardDriveDownload, Loader2 } from "lucide-react";
import { api, DOC_LABELS, money, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { deliverFile } from "@/lib/pdfFiling";

const thisMonth = () => shopToday(true);

export default function Reports() {
  const { branch, can } = useAuth();
  const [month, setMonth] = useState(thisMonth);
  const [allBranches, setAllBranches] = useState(false);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [backupStep, setBackupStep] = useState("");

  useEffect(() => {
    api
      .get("/reports/monthly", {
        params: { month, branch: allBranches ? undefined : branch },
      })
      .then(({ data }) => setReport(data))
      .catch(() => setReport(null));
  }, [month, branch, allBranches]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/reports/monthly.csv", {
        params: { month, branch: allBranches ? undefined : branch },
        responseType: "blob",
      });
      await deliverFile(
        res.data,
        "Accountant Export",
        `dhunna-${allBranches ? "all" : branch.toLowerCase()}-${month}.csv`,
        toast
      );
    } catch (e) {
      toast.error("Could not build the spreadsheet");
    } finally {
      setBusy(false);
    }
  };

  const backup = async () => {
    setBackingUp(true);
    try {
      // The backup comes down in parts: one file of records, then the PDFs and photos
      // in batches, so no single download runs long enough to be cut off.
      const { data: plan } = await api.get("/backup/plan");
      const total = plan.parts.length;
      for (const p of plan.parts) {
        setBackupStep(`File ${p.part} of ${total} — ${p.label}`);
        const res = await api.get("/backup/part.zip", {
          params: { kind: p.kind, skip: p.skip || 0, take: p.take || 0 },
          responseType: "blob",
        });
        const n = String(p.part).padStart(2, "0");
        await deliverFile(res.data, "Backups",
          `dhunna-backup-${plan.stamp}-${n}-of-${String(total).padStart(2, "0")}.zip`,
          toast);
      }
      toast.success(`Backup saved as ${total} file${total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the backup");
    } finally {
      setBackingUp(false);
      setBackupStep("");
    }
  };

  const t = report?.totals;

  return (
    <div className="fade-up space-y-7" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {allBranches ? "BOTH STORES" : branch}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Accountant Export</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Monthly sales, GST and platform handling fees, ready for your bookkeeper.
          </p>
        </div>
        <Button
          onClick={download}
          disabled={busy || !report?.rows?.length}
          className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          data-testid="download-csv-button"
        >
          <Download className="mr-2 h-4 w-4" /> Download spreadsheet
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4 border border-border p-5">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Month
          </Label>
          <Input
            type="month"
            className="h-10 w-[190px] rounded-none"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="report-month-input"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            className="accent-[#C5A059]"
            data-testid="report-all-branches-checkbox"
          />
          Include both stores
        </label>
      </div>

      <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 xl:grid-cols-5">
        {[
          ["Documents", t?.count ?? 0, "count"],
          ["Items total", money(t?.items_total), "items"],
          ["Platform handling fees", money(t?.platform_fee), "fees"],
          ["GST collected", money(t?.gst_amount), "gst"],
          ["Grand total", money(t?.total), "total"],
        ].map(([label, value, key]) => (
          <div key={key} className="bg-white p-5" data-testid={`report-stat-${key}`}>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-3 text-2xl font-bold">{value}</div>
          </div>
        ))}
      </div>

      {report?.loans?.count > 0 && (
        <div
          className="border border-border bg-[#FBF8F2] px-5 py-4 text-sm"
          data-testid="report-loans-note"
        >
          <span className="font-semibold">{money(report.loans.lent)}</span> was lent on{" "}
          {report.loans.count} pledge loan{report.loans.count === 1 ? "" : "s"} this
          month. Money lent is not a sale and carries no GST, so it is kept out of the
          figures above and appears on its own line at the bottom of the spreadsheet.
        </div>
      )}

      {report?.old_gold?.amount > 0 && (
        <div
          className="border border-border bg-[#FBF8F2] px-5 py-4 text-sm"
          data-testid="report-old-gold-note"
        >
          <span className="font-semibold">{money(report.old_gold.amount)}</span> of old
          gold was bought this month
          {report.old_gold.contracts.count > 0 &&
            ` on ${report.old_gold.contracts.count} buying contract${
              report.old_gold.contracts.count === 1 ? "" : "s"
            }`}
          {report.old_gold.trade_ins.count > 0 &&
            `${report.old_gold.contracts.count > 0 ? " plus" : " as"} ${
              report.old_gold.trade_ins.count
            } part-exchange trade-in${
              report.old_gold.trade_ins.count === 1 ? "" : "s"
            } on invoices`}
          . Old gold is a purchase, not a sale — it carries no GST and is kept out of the
          figures above. GST on a sale is worked out on the full price, so old gold taken
          in part-payment comes off after GST, never reducing the tax. See the Old Gold
          report for full ID and outcome detail; it also has its own line at the bottom of
          the spreadsheet.
        </div>
      )}

      {report?.totals?.rounding_total > 0 && (
        <div
          className="border border-border bg-[#FBF8F2] px-5 py-4 text-sm"
          data-testid="report-rounding-note"
        >
          <span className="font-semibold">{money(report.totals.rounding_total)}</span> was
          rounded off this month — small balances waived at the counter. These are never
          printed on customer invoices and are already taken out of "Net received", so your
          takings reconcile. They also appear on their own line at the bottom of the
          spreadsheet.
        </div>
      )}

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[900px] text-sm" data-testid="report-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">No.</th>
              <th className="px-3 py-2.5 text-left font-semibold">Type</th>
              <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
              <th className="px-3 py-2.5 text-left font-semibold">Paid by</th>
              <th className="px-3 py-2.5 text-right font-semibold">Items</th>
              <th className="px-3 py-2.5 text-right font-semibold">Platform handling fee</th>
              <th className="px-3 py-2.5 text-right font-semibold">GST</th>
              <th className="px-3 py-2.5 text-right font-semibold">Total</th>
              <th className="px-3 py-2.5 text-right font-semibold">Old gold</th>
              <th className="px-3 py-2.5 text-right font-semibold">Net received</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {!report?.rows?.length && (
              <tr>
                <td colSpan={11} className="px-3 py-10 text-center text-muted-foreground">
                  <FileSpreadsheet className="mx-auto mb-2 h-5 w-5 text-[#C5A059]" />
                  No records for this month.
                </td>
              </tr>
            )}
            {(report?.rows || []).map((r) => (
              <tr key={`${r.branch}-${r.doc_number}`} className="hover:bg-[#FBF8F2]">
                <td className="tabular px-3 py-2">{r.date}</td>
                <td className="tabular px-3 py-2 font-semibold">#{r.doc_number}</td>
                <td className="px-3 py-2">{DOC_LABELS[r.doc_type]}</td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td className="px-3 py-2 text-xs uppercase">{r.payment_method || "-"}</td>
                <td className="tabular px-3 py-2 text-right">{money(r.items_total)}</td>
                <td className="tabular px-3 py-2 text-right">{money(r.platform_fee)}</td>
                <td className="tabular px-3 py-2 text-right">{money(r.gst_amount)}</td>
                <td className="tabular px-3 py-2 text-right font-semibold">{money(r.total)}</td>
                <td className="tabular px-3 py-2 text-right text-muted-foreground">
                  {r.trade_in_total > 0 ? `-${money(r.trade_in_total)}` : "-"}
                </td>
                <td className="tabular px-3 py-2 text-right">{money(r.net_received)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {can("backup:export") && (
        <div
          className="flex flex-wrap items-center justify-between gap-5 border border-[#C5A059]/40 bg-[#FBF8F2] p-6"
          data-testid="backup-card"
        >
          <div className="max-w-xl">
            <h2 className="text-base font-bold md:text-lg">Full data backup</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Every invoice, contract, loan, repair and KYC form as a printable PDF, all
              customer ID photos and signatures, plus spreadsheets and a complete JSON
              copy of your records. It saves as several ZIP files — the first holds every
              record and is a complete copy on its own, the rest carry the PDFs and
              photos. Keep them together on your own drive.
            </p>
            {backupStep && (
              <p className="mt-2 text-sm font-semibold text-[#8A6A22]" data-testid="backup-progress">
                {backupStep}
              </p>
            )}
          </div>
          <Button
            onClick={backup}
            disabled={backingUp}
            variant="outline"
            className="h-11 rounded-none border-[#C5A059] px-6 font-semibold"
            data-testid="download-backup-button"
          >
            {backingUp ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <HardDriveDownload className="mr-2 h-4 w-4" />
            )}
            {backingUp ? "Packing your backup…" : "Download full backup"}
          </Button>
        </div>
      )}
    </div>
  );
}